import React, { useState, useEffect, useCallback } from "react";
import { Table, Tag } from "antd";
import { Link } from "react-router-dom";
import Modal from "@mui/material/Modal";
import Backdrop from "@mui/material/Backdrop";
import Fade from "@mui/material/Fade";
import Box from "@mui/material/Box";
import { Button, Menu, MenuItem } from "@mui/material";
import { HiDotsVertical } from "react-icons/hi";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import moment from "moment";
import SidebarNav from "../sidebar";
import AffiliateForm from "./AffiliateForm";
import TableToolbar from "../commoncomponent/TableToolbar";
import FilterPanel from "../commoncomponent/FilterPanel";
import { useTableSettings } from "../../../hooks/useTableSettings";
import { getAffiliateCodes, deleteAffiliateCode } from "../../../service/api";
import { usePermission } from "../../../hooks/usePermission";

const deleteModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 440,
  bgcolor: "background.paper",
  borderRadius: "10px",
  boxShadow: 24,
  p: 4,
};

const FRONTEND_URL =
  process.env.REACT_APP_FRONTEND_URL || "https://paraacrypto.com";

const ALL_COLUMNS = [
  {
    key: "code",
    title: "Code",
    defaultVisible: true,
    render: (r) => <strong style={{ fontFamily: "monospace", fontSize: 13 }}>{r.code}</strong>,
  },
  {
    key: "name",
    title: "Name",
    defaultVisible: true,
    render: (r) => r.name || "—",
  },
  {
    key: "isActive",
    title: "Status",
    defaultVisible: true,
    render: (r) => (
      <Tag color={r.isActive ? "green" : "red"}>
        {r.isActive ? "Active" : "Inactive"}
      </Tag>
    ),
  },
  {
    key: "redirectUrl",
    title: "Redirect URL",
    defaultVisible: true,
    render: (r) =>
      r.redirectUrl ? (
        <a
          href={r.redirectUrl}
          target="_blank"
          rel="noopener noreferrer"
          title={r.redirectUrl}
          style={{ fontSize: 13 }}
        >
          {r.redirectUrl.length > 40
            ? `${r.redirectUrl.slice(0, 40)}…`
            : r.redirectUrl}
        </a>
      ) : (
        <span style={{ color: "#aaa" }}>—</span>
      ),
  },
  {
    key: "createdAt",
    title: "Created",
    defaultVisible: true,
    render: (r) =>
      r.createdAt ? moment(r.createdAt).format("DD/MM/YYYY") : "—",
  },
];

const AffiliateCodes = () => {
  const [affiliateList, setAffiliateList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Three-dot menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const handleMenuOpen = (e, row) => { setMenuRow(row); setAnchorEl(e.currentTarget); };
  const handleMenuClose = () => { setAnchorEl(null); setMenuRow(null); };

  // Filter state
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const { canCreate, canUpdate, canDelete } = usePermission();

  const {
    pageSize,
    setPageSize,
    pageSizeOptions,
    visibleColumnKeys,
    toggleColumn,
    resetColumns,
    dynamicColumns,
  } = useTableSettings({ storageKey: "affiliate-codes", allColumns: ALL_COLUMNS });

  const fetchAffiliateCodes = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        sizePerPage: pageSize,
        pagination: "true",
        ...(search ? { search } : {}),
      };
      const res = await getAffiliateCodes(params);
      if (res?.status && res?.data) {
        let list = res.data.result || [];
        // Client-side status filter (no dedicated backend param)
        if (statusFilter === "active") list = list.filter((r) => r.isActive);
        if (statusFilter === "inactive") list = list.filter((r) => !r.isActive);
        setAffiliateList(list);
        setTotal(res.data.total || 0);
      }
    } catch {
      toast.error("Failed to fetch affiliate codes");
    }
    setLoading(false);
  }, [page, pageSize, search, statusFilter]);

  useEffect(() => {
    fetchAffiliateCodes();
  }, [fetchAffiliateCodes]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [pageSize, search, statusFilter]);

  const handleCopyLink = (code) => {
    const link = `${FRONTEND_URL}/affiliate?code=${code}`;
    navigator.clipboard.writeText(link).then(
      () => toast.success("Affiliate link copied to clipboard"),
      () => toast.error("Failed to copy link"),
    );
  };

  const handleEdit = (record) => {
    setEditId(record._id);
    setShowForm(true);
  };

  const handleDeleteOpen = (record) => {
    setSelectedAffiliate(record);
    setOpenDelete(true);
  };

  const handleDeleteClose = () => {
    setOpenDelete(false);
    setSelectedAffiliate(null);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAffiliate) return;
    setDeleteLoading(true);
    try {
      const res = await deleteAffiliateCode(selectedAffiliate._id);
      if (res?.status) {
        toast.success(res?.message || "Affiliate code deleted successfully");
        handleDeleteClose();
        fetchAffiliateCodes();
      } else {
        toast.error(res?.message || "Failed to delete");
      }
    } catch (e) {
      toast.error(e?.data?.message || "Something went wrong");
    }
    setDeleteLoading(false);
  };

  const handleBack = () => {
    setShowForm(false);
    setEditId(null);
    fetchAffiliateCodes();
  };

  const handleClearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setPage(1);
  };

  if (showForm) {
    return (
      <>
        <SidebarNav />
        <div className="page-wrapper">
          <AffiliateForm onBack={handleBack} editId={editId} />
        </div>
      </>
    );
  }

  const actionColumn = {
    key: "__actions",
    title: "Actions",
    className: "text-end",
    render: (_, row) => (
      <div className="text-end">
        <Button
          onClick={(e) => handleMenuOpen(e, row)}
          style={{ minWidth: 0, padding: 6 }}
        >
          <HiDotsVertical size={18} />
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={menuOpen && menuRow?._id === row?._id}
          onClose={handleMenuClose}
          PaperProps={{
            elevation: 4,
            sx: {
              borderRadius: 2,
              mt: 1,
              boxShadow: "0px 4px 16px rgba(0,0,0,0.1)",
              minWidth: 180,
            },
          }}
        >
          <MenuItem
            sx={{ fontSize: 14 }}
            onClick={() => { handleMenuClose(); handleCopyLink(row.code); }}
          >
            <i className="fe fe-copy me-2" style={{ color: "#17a2b8" }} />
            Copy Affiliate Link
          </MenuItem>
          {canUpdate("affiliate") && (
            <MenuItem
              sx={{ fontSize: 14 }}
              onClick={() => { handleMenuClose(); handleEdit(row); }}
            >
              <i className="fe fe-edit me-2" style={{ color: "#28a745" }} />
              Edit
            </MenuItem>
          )}
          {canDelete("affiliate") && (
            <MenuItem
              sx={{ fontSize: 14, color: "#dc2626" }}
              onClick={() => { handleMenuClose(); handleDeleteOpen(row); }}
            >
              <i className="fe fe-trash-2 me-2" />
              Delete
            </MenuItem>
          )}
        </Menu>
      </div>
    ),
  };

  const columns = [...dynamicColumns, actionColumn];

  return (
    <>
      <SidebarNav />
      <div className="page-wrapper">
        <div className="content container-fluid">

          {/* Page header */}
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title">Affiliate Codes</h3>
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/admin">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item active">Affiliate Codes</li>
                </ul>
              </div>
              <div className="col-auto">
                {canCreate("affiliate") && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => { setEditId(null); setShowForm(true); }}
                  >
                    <i className="fe fe-plus me-1" />
                    Add Affiliate Code
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="row">
            {/* Table */}
            <div className="col-xl-9 col-lg-8 col-md-12">
              <div className="card card-table">
                <div className="card-body">
                  <TableToolbar
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                    total={total}
                    page={page}
                    allColumns={ALL_COLUMNS}
                    visibleColumnKeys={visibleColumnKeys}
                    toggleColumn={toggleColumn}
                    resetColumns={resetColumns}
                    onRefresh={fetchAffiliateCodes}
                    refreshLoading={loading}
                  />
                  <Table
                    dataSource={affiliateList}
                    columns={columns}
                    rowKey={(r) => r._id}
                    loading={loading}
                    pagination={{
                      current: page,
                      total,
                      pageSize,
                      onChange: (p) => setPage(p),
                      showSizeChanger: false,
                      showTotal: (t, range) =>
                        `${range[0]}-${range[1]} of ${t}`,
                    }}
                    locale={{ emptyText: "No affiliate codes found" }}
                    scroll={{ x: true }}
                  />
                </div>
              </div>
            </div>

            {/* Filter sidebar */}
            <div className="col-xl-3 col-lg-4 col-md-12">
              <FilterPanel onClear={handleClearFilters} count={total}>

                <FilterPanel.Field label="Search">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Code or name…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </FilterPanel.Field>

                <FilterPanel.Field label="Status">
                  <select
                    className="form-select form-select-sm"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All statuses</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </FilterPanel.Field>

              </FilterPanel>
            </div>
          </div>
        </div>
      </div>

      {/* Delete modal */}
      <Modal
        open={openDelete}
        onClose={handleDeleteClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 400 }}
      >
        <Fade in={openDelete}>
          <Box sx={deleteModalStyle}>
            <h5 style={{ marginBottom: 8 }}>Delete Affiliate Code</h5>
            <p className="text-muted mb-3">
              Are you sure you want to delete{" "}
              <strong>{selectedAffiliate?.code}</strong>? This action cannot be
              undone.
            </p>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleDeleteClose}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDeleteConfirm}
                disabled={deleteLoading}
              >
                {deleteLoading ? <Spinner size="sm" /> : "Delete"}
              </button>
            </div>
          </Box>
        </Fade>
      </Modal>
    </>
  );
};

export default AffiliateCodes;
